/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { IconButton } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import SearchBar from 'bundles/search-common/components/searchbar/consumer/SearchBar';
import { useMobileWebConversationalDiscoveryExperiment } from 'bundles/unified-common/contexts/MobileWebConversationalDiscoveryExperimentContext';

const styles = {
  mobileSearchPage: css`
    position: fixed;
    top: -120vh;
    left: 0;
    background-color: transparent;
    height: 100vh;
    padding-top: 65px;
    transition: transform 0.5s ease-in-out;
    overflow: visible;
    z-index: 1;
  `,
  openMobileSearch: css`
    transform: translateY(120vh);
  `,
  mobileSearchBodyWrapper: css`
    background-color: var(--cds-color-neutral-background-primary);
    height: 100vh;
    width: 100vw;
    overflow: scroll;
  `,
  searchIcon: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

const MobileSearchBarGridItem = () => {
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  // Matches the icon row's tight Figma spacing in the mobile-web conversational-discovery
  // treatment (MERCH-3449) by dropping this button's own hit-box padding (48px -> 24px, still
  // WCAG 2.2 AA's 24px target-size floor). This component is shared far beyond XDP, so the drop
  // stays gated to the treatment rather than changing every other caller's search icon.
  const { mobileWebConversationalDiscoveryEnabled } = useMobileWebConversationalDiscoveryExperiment();

  useEffect(() => {
    const bodyStyle = document.body.style;
    bodyStyle.overflow = searchIsOpen ? 'hidden' : 'visible';
    return () => {
      bodyStyle.overflow = 'visible';
    };
  }, [searchIsOpen]);

  const disableScrolling = (disabled: boolean) => {
    const bodyStyle = document?.body?.style;
    if (bodyStyle) {
      bodyStyle.overflow = disabled ? 'hidden' : 'visible';
    }
  };

  const showSearch = () => {
    setSearchIsOpen(true);
    disableScrolling(true);
  };

  const hideSearch = () => {
    setSearchIsOpen(false);
    disableScrolling(false);
  };

  return (
    <React.Fragment>
      {!searchIsOpen && (
        <IconButton
          intent="search"
          variant="ghost"
          onClick={showSearch}
          css={styles.searchIcon}
          edgeAlign={mobileWebConversationalDiscoveryEnabled ? 'noPadding' : undefined}
        />
      )}
      <div css={[styles.mobileSearchPage, searchIsOpen && styles.openMobileSearch]}>
        <div css={styles.mobileSearchBodyWrapper}>
          {searchIsOpen && (
            <TrackedDiv trackingName="mobile_search_contents" trackClicks={false} withVisibilityTracking>
              <SearchBar searchIsOpen={searchIsOpen} hideMobileSearchPage={hideSearch} />
            </TrackedDiv>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default MobileSearchBarGridItem;
